import { useState, useEffect } from 'react'
import { supabase } from '../utils/supabase'

export interface ColorSettings {
  primary_color: string
  secondary_color: string
  accent_color: string
  warm_color: string
  nature_color: string
  neutral_color: string
  text_primary: string
  text_secondary: string
  text_muted: string
  background_light: string
  border_color: string
  success_color: string
  warning_color: string
  error_color: string
  info_color: string
}

export interface Setting {
  id: number
  setting_key: string
  setting_value: string
  setting_type: string
  category: string
  description: string
  is_active: boolean
  created_at: string
  updated_at: string
}

const defaultColors: ColorSettings = {
  primary_color: '#DC143C',
  secondary_color: '#FFDB58',
  accent_color: '#4B0082',
  warm_color: '#FF8C42',
  nature_color: '#228B22',
  neutral_color: '#F5F5F5',
  text_primary: '#111827',
  text_secondary: '#6B7280',
  text_muted: '#9CA3AF',
  background_light: '#F9FAFB',
  border_color: '#E5E7EB',
  success_color: '#10B981',
  warning_color: '#F59E0B',
  error_color: '#EF4444',
  info_color: '#3B82F6'
}

export const useColorSettings = () => {
  const [colors, setColors] = useState<ColorSettings>(defaultColors)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Fetch color settings from database
  const fetchColors = async () => {
    try {
      setLoading(true)
      setError(null)

      const { data, error: fetchError } = await supabase
        .from('settings')
        .select('*')
        .eq('category', 'colors')
        .eq('is_active', true)

      if (fetchError) {
        throw fetchError
      }

      if (data && data.length > 0) {
        const colorSettings: Partial<ColorSettings> = {}
        
        data.forEach((setting: Setting) => {
          if (setting.setting_key in defaultColors) {
            colorSettings[setting.setting_key as keyof ColorSettings] = setting.setting_value
          }
        })

        setColors({ ...defaultColors, ...colorSettings })
      }
    } catch (err) {
      console.error('Error fetching color settings:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch color settings')
      // Use default colors on error
      setColors(defaultColors)
    } finally {
      setLoading(false)
    }
  }

  // Update a single color setting
  const updateColor = async (colorKey: keyof ColorSettings, colorValue: string) => {
    try {
      setError(null)

      const { error: updateError } = await supabase
        .from('settings')
        .update({ 
          setting_value: colorValue,
          updated_at: new Date().toISOString()
        })
        .eq('setting_key', colorKey)

      if (updateError) {
        throw updateError
      }

      // Update local state
      setColors(prev => ({
        ...prev,
        [colorKey]: colorValue
      }))

      // Apply the color to CSS custom properties immediately
      applyColorToCSSVariables(colorKey, colorValue)

      return { success: true }
    } catch (err) {
      console.error('Error updating color setting:', err)
      setError(err instanceof Error ? err.message : 'Failed to update color setting')
      return { success: false, error: err instanceof Error ? err.message : 'Update failed' }
    }
  }

  // Update multiple colors at once
  const updateColors = async (newColors: Partial<ColorSettings>) => {
    try {
      setError(null)

      const updates = Object.entries(newColors).map(([key, value]) => ({
        setting_key: key,
        setting_value: value,
        updated_at: new Date().toISOString()
      }))

      const { error: updateError } = await supabase
        .from('settings')
        .upsert(updates, { onConflict: 'setting_key' })

      if (updateError) {
        throw updateError
      }

      // Update local state
      setColors(prev => ({
        ...prev,
        ...newColors
      }))

      // Apply colors to CSS custom properties
      Object.entries(newColors).forEach(([key, value]) => {
        applyColorToCSSVariables(key as keyof ColorSettings, value)
      })

      return { success: true }
    } catch (err) {
      console.error('Error updating color settings:', err)
      setError(err instanceof Error ? err.message : 'Failed to update color settings')
      return { success: false, error: err instanceof Error ? err.message : 'Update failed' }
    }
  }

  // Helper function to convert hex to rgba
  const hexToRgba = (hex: string, alpha: number): string => {
    const r = parseInt(hex.slice(1, 3), 16)
    const g = parseInt(hex.slice(3, 5), 16)
    const b = parseInt(hex.slice(5, 7), 16)
    return `rgba(${r}, ${g}, ${b}, ${alpha})`
  }

  // Apply colors to CSS custom properties for dynamic theming
  const applyColorToCSSVariables = (colorKey: keyof ColorSettings, colorValue: string) => {
    const root = document.documentElement
    
    // Map color keys to CSS custom property names
    const cssVariableMap: Record<keyof ColorSettings, string> = {
      primary_color: '--color-primary',
      secondary_color: '--color-secondary',
      accent_color: '--color-accent',
      warm_color: '--color-warm',
      nature_color: '--color-nature',
      neutral_color: '--color-neutral',
      text_primary: '--color-text-primary',
      text_secondary: '--color-text-secondary',
      text_muted: '--color-text-muted',
      background_light: '--color-background-light',
      border_color: '--color-border',
      success_color: '--color-success',
      warning_color: '--color-warning',
      error_color: '--color-error',
      info_color: '--color-info'
    }

    const cssVariable = cssVariableMap[colorKey]
    if (cssVariable) {
      root.style.setProperty(cssVariable, colorValue)
      
      // For primary color, also update related rgba values used in CSS
      if (colorKey === 'primary_color') {
        root.style.setProperty('--color-primary-rgb-30', hexToRgba(colorValue, 0.3))
        root.style.setProperty('--color-primary-rgb-20', hexToRgba(colorValue, 0.2))
        root.style.setProperty('--color-primary-rgb-05', hexToRgba(colorValue, 0.05))
        root.style.setProperty('--color-primary-rgb-50', hexToRgba(colorValue, 0.5))
      }
      
      // For secondary color, also update related rgba values
      if (colorKey === 'secondary_color') {
        root.style.setProperty('--color-secondary-rgb-05', hexToRgba(colorValue, 0.05))
      }
      
      // For border color, also update related rgba values
      if (colorKey === 'border_color') {
        root.style.setProperty('--color-border-rgb-50', hexToRgba(colorValue, 0.5))
      }
    }
  }

  // Apply all colors to CSS custom properties
  const applyAllColorsToCSSVariables = () => {
    Object.entries(colors).forEach(([key, value]) => {
      applyColorToCSSVariables(key as keyof ColorSettings, value)
    })
  }

  // Reset colors to defaults
  const resetColors = async () => {
    try {
      setError(null)

      const updates = Object.entries(defaultColors).map(([key, value]) => ({
        setting_key: key,
        setting_value: value,
        updated_at: new Date().toISOString()
      }))

      const { error: updateError } = await supabase
        .from('settings')
        .upsert(updates, { onConflict: 'setting_key' })

      if (updateError) {
        throw updateError
      }

      setColors(defaultColors)
      applyAllColorsToCSSVariables()

      return { success: true }
    } catch (err) {
      console.error('Error resetting color settings:', err)
      setError(err instanceof Error ? err.message : 'Failed to reset color settings')
      return { success: false, error: err instanceof Error ? err.message : 'Reset failed' }
    }
  }

  // Get a specific color value
  const getColor = (colorKey: keyof ColorSettings): string => {
    return colors[colorKey] || defaultColors[colorKey]
  }

  // Get colors in different formats
  const getColorsAsCSS = (): string => {
    return Object.entries(colors)
      .map(([key, value]) => {
        const cssKey = key.replace(/_/g, '-')
        return `--color-${cssKey}: ${value};`
      })
      .join('\n')
  }

  // Initialize colors on mount
  useEffect(() => {
    fetchColors()
  }, [])

  // Apply colors to CSS variables when colors change
  useEffect(() => {
    if (!loading) {
      applyAllColorsToCSSVariables()
    }
  }, [colors, loading])

  return {
    colors,
    loading,
    error,
    updateColor,
    updateColors,
    resetColors,
    getColor,
    getColorsAsCSS,
    fetchColors,
    defaultColors
  }
}

export default useColorSettings